import { create } from 'zustand'
import { getOutputPreset, OutputPresetId } from '@/content/instagram'
import {
  SplitKind,
  NodeKind,
  TransformKind,
  assertSplitSettings,
  createImageDocument,
  fitCollageCells,
  findNode,
  firstImageNodeOrNull,
  firstRenderedImageNodeOrNull,
  findRenderedNode,
  intrinsicRatio,
  outputRatio,
  presetIdForSplit,
  replaceNode,
  validateDocument,
  type CompositionNode,
  type Document,
  type NodeId,
  type SourceId,
  type SplitSettings,
} from '@/core/document'
import { ExportFormat, ResizeMode, type ExportOptions } from '@/core/export/types'
import { validateExportOptions } from '@/core/export/validate'
import {
  FIT_VIEW_TRANSFORM,
  assertViewTransform,
  centeredPresetRectInside,
  largestRectForRatioInside,
  orientationCropBounds,
  type ViewTransform,
} from '@/core/geometry'
import {
  EMPTY_HISTORY,
  EditAction,
  pushHistory,
  redoHistory,
  snapshotsEqual,
  undoHistory,
  type HistorySnapshot,
  type HistoryState,
} from '@/core/history'
import { disposeSource, type SourceImage } from '@/core/image/SourceImage'
import { PersistenceStatus } from '@/core/persistence/types'
import { addBreadcrumb, trackUsage } from '@/core/reporting/runtime'
import { ReportingEvent, UsageEvent } from '@/core/reporting/types'
import {
  BottomSheetSnap,
  MorePanelId,
  OverlayId,
  PREVIEW_OLDER_POST_OFFSETS,
  PreviewTab,
  ThemeMode,
  ToolTab,
  type PreviewOlderPostOffset,
  type QualityLoupePoint,
} from '@/core/store/uiState'

export interface EditSessionState {
  sources: ReadonlyMap<SourceId, SourceImage>
  document: Document | null
  selectedNodeId: NodeId | null
  viewTransform: ViewTransform
  history: HistoryState
  committedSnapshot: HistorySnapshot | null
  persistedEditId: string | null
  persistenceStatus: PersistenceStatus
  exportOptions: ExportOptions
  overlaysVisible: ReadonlySet<OverlayId>
  handlesVisible: boolean
  isExporting: boolean
  isLoading: boolean
  themeMode: ThemeMode
  mobileSheetSnap: BottomSheetSnap
  activeTab: ToolTab
  previewOpen: boolean
  previewTab: PreviewTab
  previewOlderPostOffset: PreviewOlderPostOffset
  qualityViewOpen: boolean
  qualityLoupePoint: QualityLoupePoint
  qualityDiffVisible: boolean
  recentsOpen: boolean
  settingsOpen: boolean
  moreSectionsOpen: ReadonlySet<MorePanelId>
  recoveryBlockedEditId: string | null
  loadSource: (source: SourceImage) => void
  restoreSession: (
    sources: ReadonlyMap<SourceId, SourceImage>,
    document: Document,
    selectedNodeId: NodeId | null,
    exportOptions: ExportOptions,
    persistedEditId: string,
  ) => void
  clearSource: () => void
  selectNode: (id: NodeId) => void
  setOutputPreset: (presetId: OutputPresetId) => void
  setSplit: (split: SplitSettings | null) => void
  replaceDocument: (
    document: Document,
    selectedNodeId: NodeId | null,
    addedSources?: ReadonlyMap<SourceId, SourceImage>,
  ) => void
  updateNode: (id: NodeId, updater: (node: CompositionNode) => CompositionNode) => void
  wrapNode: (id: NodeId, wrapper: CompositionNode) => void
  unwrapNode: (id: NodeId, child: CompositionNode) => void
  setViewTransform: (transform: ViewTransform) => void
  commitHistory: (action?: EditAction) => void
  undo: () => EditAction
  redo: () => EditAction
  setPersistedEditId: (id: string | null) => void
  setPersistenceStatus: (status: PersistenceStatus) => void
  setExportOptions: (partial: Partial<ExportOptions>) => void
  setOverlayVisible: (id: OverlayId, visible: boolean) => void
  setHandlesVisible: (visible: boolean) => void
  setIsExporting: (isExporting: boolean) => void
  setIsLoading: (isLoading: boolean) => void
  setThemeMode: (themeMode: ThemeMode) => void
  setMobileSheetSnap: (snap: BottomSheetSnap) => void
  setActiveTab: (tab: ToolTab) => void
  setPreviewOpen: (open: boolean) => void
  setPreviewTab: (tab: PreviewTab) => void
  setPreviewOlderPostOffset: (offset: PreviewOlderPostOffset) => void
  setQualityViewOpen: (open: boolean) => void
  setQualityLoupePoint: (point: QualityLoupePoint) => void
  setQualityDiffVisible: (visible: boolean) => void
  setRecentsOpen: (open: boolean) => void
  setSettingsOpen: (open: boolean) => void
  setMoreSectionOpen: (id: MorePanelId, open: boolean) => void
  setRecoveryBlockedEditId: (id: string | null) => void
}

function defaultOverlaysForPreset(presetId: OutputPresetId): ReadonlySet<OverlayId> {
  return presetId === OutputPresetId.Story
    ? new Set([OverlayId.SafeZones])
    : new Set()
}

export const DEFAULT_EXPORT_OPTIONS: Readonly<ExportOptions> = {
  format: ExportFormat.Jpeg,
  jpegQuality: 0.95,
  resizeMode: ResizeMode.Original,
  customLongestSide: null,
}

function disposeSources(sources: ReadonlyMap<SourceId, SourceImage>, except?: SourceImage): void {
  const disposed = new Set<SourceImage>()
  for (const source of sources.values()) {
    if (source !== except && !disposed.has(source)) {
      disposeSource(source)
      disposed.add(source)
    }
  }
}

function requireDocument(state: EditSessionState, action: string): Document {
  if (!state.document) throw new Error(`${action} requires a loaded document`)
  return state.document
}

function historySnapshot(state: EditSessionState, action: string): HistorySnapshot {
  return {
    document: requireDocument(state, action),
    selectedNodeId: state.selectedNodeId,
  }
}

function validateHistorySnapshot(state: EditSessionState, snapshot: HistorySnapshot): void {
  validateDocument(snapshot.document, state.sources)
  if (snapshot.selectedNodeId) {
    const selected = findNode(snapshot.document.root, snapshot.selectedNodeId)
    if (!selected) throw new Error(`History snapshot selects unknown node "${snapshot.selectedNodeId}"`)
    if (selected.kind !== NodeKind.Image) throw new Error('History snapshots may select only Image nodes')
    if (!findRenderedNode(snapshot.document.root, selected.id)) throw new Error('History snapshots may select only rendered photos')
  }
}

function updatedDocument(
  state: EditSessionState,
  id: NodeId,
  updater: (node: CompositionNode) => CompositionNode,
): Document {
  const document = requireDocument(state, 'Node update')
  const current = findNode(document.root, id)
  if (!current) throw new Error(`Cannot update unknown node "${id}"`)
  const next = updater(current)
  if (next.id !== id) throw new Error('A node update must preserve the node id')
  const updated = { ...document, root: replaceNode(document.root, id, next) }
  validateDocument(updated, state.sources)
  return updated
}

export const useEditSession = create<EditSessionState>((set, get) => ({
  sources: new Map(),
  document: null,
  selectedNodeId: null,
  viewTransform: { ...FIT_VIEW_TRANSFORM },
  history: EMPTY_HISTORY,
  committedSnapshot: null,
  persistedEditId: null,
  persistenceStatus: PersistenceStatus.Idle,
  exportOptions: { ...DEFAULT_EXPORT_OPTIONS },
  overlaysVisible: new Set(),
  handlesVisible: true,
  isExporting: false,
  isLoading: false,
  themeMode: ThemeMode.System,
  mobileSheetSnap: BottomSheetSnap.Half,
  activeTab: ToolTab.Crop,
  previewOpen: false,
  previewTab: PreviewTab.Profile,
  previewOlderPostOffset: 0,
  qualityViewOpen: false,
  qualityLoupePoint: null,
  qualityDiffVisible: false,
  recentsOpen: false,
  settingsOpen: false,
  moreSectionsOpen: new Set(),
  recoveryBlockedEditId: null,
  loadSource: (source) => {
    let created: ReturnType<typeof createImageDocument>
    try {
      created = createImageDocument(source)
    } catch (error) {
      disposeSource(source)
      throw error
    }
    disposeSources(get().sources, source)
    const committedSnapshot: HistorySnapshot = {
      document: created.document,
      selectedNodeId: created.rootId,
    }
    set({
      sources: created.sources,
      document: created.document,
      selectedNodeId: created.rootId,
      viewTransform: { ...FIT_VIEW_TRANSFORM },
      history: EMPTY_HISTORY,
      committedSnapshot,
      persistedEditId: null,
      persistenceStatus: PersistenceStatus.Idle,
      overlaysVisible: defaultOverlaysForPreset(created.document.output.presetId),
      isExporting: false,
      activeTab: ToolTab.Crop,
      previewOpen: false,
      previewTab: PreviewTab.Profile,
      previewOlderPostOffset: 0,
      qualityViewOpen: false,
      qualityLoupePoint: null,
      qualityDiffVisible: false,
      recentsOpen: false,
      settingsOpen: false,
      recoveryBlockedEditId: null,
    })
    addBreadcrumb(ReportingEvent.SourceLoaded, { mimeType: source.mimeType })
    trackUsage(UsageEvent.SourceLoaded, {
      mimeType: source.mimeType,
      megapixels: Math.round(source.width * source.height / 100_000) / 10,
    })
    trackUsage(UsageEvent.ToolUsed, { toolId: ToolTab.Crop })
  },
  restoreSession: (sources, document, selectedNodeId, exportOptions, persistedEditId) => {
    if (!persistedEditId.trim()) throw new Error('Restored session requires a persisted edit id')
    validateDocument(document, sources)
    validateExportOptions(exportOptions)
    const restoredSelection = selectedNodeId ? findNode(document.root, selectedNodeId) : null
    if (selectedNodeId && !restoredSelection) throw new Error(`Restored session selects unknown node "${selectedNodeId}"`)
    const activePhotoId = restoredSelection?.kind === NodeKind.Image && findRenderedNode(document.root, restoredSelection.id)
      ? restoredSelection.id
      : firstRenderedImageNodeOrNull(document.root)?.id ?? null
    const committedSnapshot: HistorySnapshot = { document, selectedNodeId: activePhotoId }
    disposeSources(get().sources)
    set({
      sources,
      document,
      selectedNodeId: activePhotoId,
      viewTransform: { ...FIT_VIEW_TRANSFORM },
      exportOptions: { ...exportOptions },
      overlaysVisible: defaultOverlaysForPreset(document.output.presetId),
      history: EMPTY_HISTORY,
      committedSnapshot,
      persistedEditId,
      persistenceStatus: PersistenceStatus.Saved,
      isExporting: false,
      activeTab: activePhotoId ? ToolTab.Crop : ToolTab.Collage,
      previewOpen: false,
      previewTab: PreviewTab.Profile,
      previewOlderPostOffset: 0,
      qualityViewOpen: false,
      qualityLoupePoint: null,
      qualityDiffVisible: false,
      recentsOpen: false,
      settingsOpen: false,
      recoveryBlockedEditId: null,
    })
    addBreadcrumb(ReportingEvent.RecentRestored)
    trackUsage(UsageEvent.RecentRestored, {})
    trackUsage(UsageEvent.ToolUsed, { toolId: activePhotoId ? ToolTab.Crop : ToolTab.Collage })
  },
  clearSource: () => {
    disposeSources(get().sources)
    set({
      sources: new Map(),
      document: null,
      selectedNodeId: null,
      viewTransform: { ...FIT_VIEW_TRANSFORM },
      history: EMPTY_HISTORY,
      committedSnapshot: null,
      persistedEditId: null,
      persistenceStatus: PersistenceStatus.Idle,
      overlaysVisible: new Set(),
      isExporting: false,
      activeTab: ToolTab.Crop,
      previewOpen: false,
      previewTab: PreviewTab.Profile,
      previewOlderPostOffset: 0,
      qualityViewOpen: false,
      qualityLoupePoint: null,
      qualityDiffVisible: false,
      recentsOpen: false,
      settingsOpen: false,
    })
  },
  selectNode: (id) => {
    const document = requireDocument(get(), 'Node selection')
    const node = findNode(document.root, id)
    if (!node) throw new Error(`Cannot select unknown node "${id}"`)
    if (node.kind !== NodeKind.Image) throw new Error(`Active photo selection requires an Image node, received "${node.kind}"`)
    if (!findRenderedNode(document.root, id)) throw new Error(`Cannot activate photo "${id}" because it is not rendered in the composition`)
    set({ selectedNodeId: id })
  },
  setOutputPreset: (presetId) => {
    const state = get()
    const document = requireDocument(state, 'Output preset update')
    if (document.output.split) throw new Error('Output preset cannot change while a split is active')
    const preset = getOutputPreset(presetId)
    const root = document.root
    const ratio = preset.ratio ?? intrinsicRatio(root, state.sources)
    let updatedRoot = root
    if (root.kind === NodeKind.Image) {
      const source = state.sources.get(root.sourceId)
      if (!source) throw new Error(`Cannot update output preset: source "${root.sourceId}" is missing`)
      const currentCrop = root.transforms[TransformKind.Crop]
      const bounds = orientationCropBounds(source, root.transforms[TransformKind.Orientation])
      const rect = preset.id === OutputPresetId.Original
        ? bounds
        : currentCrop
          ? centeredPresetRectInside(currentCrop.rect, bounds, ratio)
          : largestRectForRatioInside(bounds, ratio)
      updatedRoot = {
        ...root,
        transforms: { ...root.transforms, [TransformKind.Crop]: { rect } },
      }
    } else if (root.kind === NodeKind.Collage) {
      updatedRoot = fitCollageCells(root, { width: ratio.w, height: ratio.h }, state.sources)
    }
    const updated: Document = {
      ...document,
      output: { ...document.output, presetId },
      root: updatedRoot,
    }
    validateDocument(updated, state.sources)
    set({
      document: updated,
      overlaysVisible: defaultOverlaysForPreset(presetId),
    })
  },
  setSplit: (split) => {
    assertSplitSettings(split)
    const state = get()
    const document = requireDocument(state, 'Output split update')
    if (document.output.presetId === OutputPresetId.Story && split !== null) {
      throw new Error('Story output does not support carousel or grid splits')
    }
    const copiedSplit: SplitSettings | null = split?.kind === SplitKind.Carousel
      ? { ...split, tileRatio: { ...split.tileRatio } }
      : split ? { ...split } : null
    const output = {
      ...document.output,
      presetId: copiedSplit ? presetIdForSplit(copiedSplit) : document.output.presetId,
      split: copiedSplit,
    }
    const root = document.root
    let updatedRoot = root
    if (root.kind === NodeKind.Image) {
      const source = state.sources.get(root.sourceId)
      if (!source) throw new Error(`Cannot update output split: source "${root.sourceId}" is missing`)
      const bounds = orientationCropBounds(source, root.transforms[TransformKind.Orientation])
      const currentCrop = root.transforms[TransformKind.Crop]
      const ratio = outputRatio(output)
      const rect = currentCrop
        ? centeredPresetRectInside(currentCrop.rect, bounds, ratio)
        : largestRectForRatioInside(bounds, ratio)
      updatedRoot = {
        ...root,
        transforms: { ...root.transforms, [TransformKind.Crop]: { rect } },
      }
    } else if (root.kind === NodeKind.Collage) {
      const ratio = outputRatio(output, intrinsicRatio(root, state.sources))
      updatedRoot = fitCollageCells(root, { width: ratio.w, height: ratio.h }, state.sources)
    }
    const updated: Document = {
      ...document,
      output,
      root: updatedRoot,
    }
    validateDocument(updated, state.sources)
    set({
      document: updated,
      overlaysVisible: defaultOverlaysForPreset(output.presetId),
    })
  },
  replaceDocument: (document, selectedNodeId, addedSources) => {
    const state = get()
    requireDocument(state, 'Document replacement')
    const sources = new Map(state.sources)
    for (const [id, source] of addedSources ?? []) {
      if (sources.has(id)) throw new Error(`Document replacement source id "${id}" already exists`)
      if (!source.fileName.trim() || source.width <= 0 || source.height <= 0) {
        throw new Error(`Document replacement source "${id}" is not configured correctly`)
      }
      sources.set(id, source)
    }
    validateDocument(document, sources)
    const requestedSelection = selectedNodeId ? findNode(document.root, selectedNodeId) : null
    if (selectedNodeId && !requestedSelection) throw new Error(`Document replacement selects unknown node "${selectedNodeId}"`)
    if (requestedSelection && requestedSelection.kind !== NodeKind.Image) {
      throw new Error(`Document replacement selection requires an Image node, received "${requestedSelection.kind}"`)
    }
    const activePhotoId = requestedSelection && findRenderedNode(document.root, requestedSelection.id)
      ? requestedSelection.id
      : firstRenderedImageNodeOrNull(document.root)?.id ?? null
    set({
      document,
      sources,
      selectedNodeId: activePhotoId,
      overlaysVisible: defaultOverlaysForPreset(document.output.presetId),
    })
  },
  updateNode: (id, updater) => {
    set({ document: updatedDocument(get(), id, updater) })
  },
  wrapNode: (id, wrapper) => {
    const state = get()
    const document = requireDocument(state, 'Node wrapping')
    const current = findNode(document.root, id)
    if (!current) throw new Error(`Cannot wrap unknown node "${id}"`)
    if (wrapper.kind !== NodeKind.Frame) throw new Error('Node wrapping requires a Frame wrapper')
    if (wrapper.child.id !== current.id) throw new Error('A node wrapper must contain the wrapped node')
    if (findNode(document.root, wrapper.id)) throw new Error(`Node wrapper id "${wrapper.id}" already exists`)
    const updated = { ...document, root: replaceNode(document.root, id, wrapper) }
    validateDocument(updated, state.sources)
    set({ document: updated, selectedNodeId: current.kind === NodeKind.Image ? current.id : firstImageNodeOrNull(current)?.id ?? null })
  },
  unwrapNode: (id, child) => {
    const state = get()
    const document = requireDocument(state, 'Node unwrapping')
    const current = findNode(document.root, id)
    if (!current) throw new Error(`Cannot unwrap unknown node "${id}"`)
    if (current.kind !== NodeKind.Frame) throw new Error('Node unwrapping requires a Frame node')
    if (child.id !== current.child.id) throw new Error('Node unwrapping must preserve the wrapped child id')
    const updated = { ...document, root: replaceNode(document.root, id, child) }
    validateDocument(updated, state.sources)
    set({ document: updated, selectedNodeId: firstImageNodeOrNull(child)?.id ?? null })
  },
  setViewTransform: (viewTransform) => {
    assertViewTransform(viewTransform)
    set({ viewTransform: { ...viewTransform } })
  },
  commitHistory: (action = EditAction.GenericEdit) => {
    if (!Object.values(EditAction).includes(action)) {
      throw new Error(`History commit received unknown edit action: "${String(action)}"`)
    }
    const state = get()
    const committed = state.committedSnapshot
    if (!committed) throw new Error('History commit requires a loaded document')
    const current = historySnapshot(state, 'History commit')
    if (snapshotsEqual(current, committed)) return
    set({
      history: pushHistory(state.history, committed, action),
      committedSnapshot: current,
    })
  },
  undo: () => {
    const state = get()
    if (!state.committedSnapshot) throw new Error('Undo requires a loaded document')
    const result = undoHistory(state.history, historySnapshot(state, 'Undo'))
    validateHistorySnapshot(state, result.restored)
    set({
      document: result.restored.document,
      selectedNodeId: result.restored.selectedNodeId,
      history: result.history,
      committedSnapshot: result.restored,
    })
    return result.action
  },
  redo: () => {
    const state = get()
    if (!state.committedSnapshot) throw new Error('Redo requires a loaded document')
    const result = redoHistory(state.history, historySnapshot(state, 'Redo'))
    validateHistorySnapshot(state, result.restored)
    set({
      document: result.restored.document,
      selectedNodeId: result.restored.selectedNodeId,
      history: result.history,
      committedSnapshot: result.restored,
    })
    return result.action
  },
  setPersistedEditId: (persistedEditId) => {
    if (persistedEditId !== null && !persistedEditId.trim()) {
      throw new Error('Persisted edit id must be null or a non-empty string')
    }
    set({ persistedEditId })
  },
  setPersistenceStatus: (persistenceStatus) => {
    if (!Object.values(PersistenceStatus).includes(persistenceStatus)) {
      throw new Error(`Invalid persistence status: "${String(persistenceStatus)}"`)
    }
    set({ persistenceStatus })
  },
  setExportOptions: (partial) => {
    const next = { ...get().exportOptions, ...partial }
    validateExportOptions(next)
    set({ exportOptions: next })
  },
  setOverlayVisible: (id, visible) => {
    if (!Object.values(OverlayId).includes(id)) {
      throw new Error(`Unknown editor overlay: "${String(id)}"`)
    }
    if (typeof visible !== 'boolean') throw new Error('Editor overlay visibility must be a boolean')
    const overlaysVisible = new Set(get().overlaysVisible)
    if (visible) overlaysVisible.add(id)
    else overlaysVisible.delete(id)
    set({ overlaysVisible })
  },
  setHandlesVisible: (handlesVisible) => {
    if (typeof handlesVisible !== 'boolean') throw new Error('Handle visibility must be a boolean')
    set({ handlesVisible })
  },
  setIsExporting: (isExporting) => {
    if (typeof isExporting !== 'boolean') throw new Error('Exporting state must be a boolean')
    set({ isExporting })
  },
  setIsLoading: (isLoading) => {
    if (typeof isLoading !== 'boolean') throw new Error('Loading state must be a boolean')
    set({ isLoading })
  },
  setThemeMode: (themeMode) => {
    if (!Object.values(ThemeMode).includes(themeMode)) {
      throw new Error(`Invalid theme mode: "${String(themeMode)}"`)
    }
    set({ themeMode })
  },
  setMobileSheetSnap: (mobileSheetSnap) => {
    if (!Object.values(BottomSheetSnap).includes(mobileSheetSnap)) {
      throw new Error(`Invalid bottom sheet snap point: "${String(mobileSheetSnap)}"`)
    }
    set({ mobileSheetSnap })
  },
  setActiveTab: (activeTab) => {
    if (!Object.values(ToolTab).includes(activeTab)) {
      throw new Error(`Invalid active tool tab: "${String(activeTab)}"`)
    }
    const changed = get().activeTab !== activeTab
    set({ activeTab })
    if (changed) {
      addBreadcrumb(ReportingEvent.ToolChanged, { toolId: activeTab })
      trackUsage(UsageEvent.ToolUsed, { toolId: activeTab })
    }
  },
  setPreviewOpen: (previewOpen) => {
    if (typeof previewOpen !== 'boolean') throw new Error('Preview open state must be a boolean')
    const opened = previewOpen && !get().previewOpen
    set({ previewOpen })
    if (opened) {
      addBreadcrumb(ReportingEvent.PreviewOpened)
      trackUsage(UsageEvent.PreviewOpened, {})
    }
  },
  setPreviewTab: (previewTab) => {
    if (!Object.values(PreviewTab).includes(previewTab)) {
      throw new Error(`Invalid preview tab: "${String(previewTab)}"`)
    }
    set({ previewTab })
  },
  setPreviewOlderPostOffset: (previewOlderPostOffset) => {
    if (!PREVIEW_OLDER_POST_OFFSETS.includes(previewOlderPostOffset)) {
      throw new Error(`Invalid preview older-post offset: "${String(previewOlderPostOffset)}"`)
    }
    set({ previewOlderPostOffset })
  },
  setQualityViewOpen: (qualityViewOpen) => {
    if (typeof qualityViewOpen !== 'boolean') throw new Error('Quality view open state must be a boolean')
    const opened = qualityViewOpen && !get().qualityViewOpen
    set({ qualityViewOpen })
    if (opened) {
      addBreadcrumb(ReportingEvent.QualityCompareOpened)
      trackUsage(UsageEvent.QualityCompareOpened, {})
    }
  },
  setQualityLoupePoint: (qualityLoupePoint) => {
    if (qualityLoupePoint && (!Number.isFinite(qualityLoupePoint.x) || !Number.isFinite(qualityLoupePoint.y))) {
      throw new Error('Quality loupe point must be null or contain finite coordinates')
    }
    set({ qualityLoupePoint: qualityLoupePoint ? { ...qualityLoupePoint } : null })
  },
  setQualityDiffVisible: (qualityDiffVisible) => {
    if (typeof qualityDiffVisible !== 'boolean') throw new Error('Quality difference visibility must be a boolean')
    set({ qualityDiffVisible })
  },
  setRecentsOpen: (recentsOpen) => {
    if (typeof recentsOpen !== 'boolean') throw new Error('Recent-edits visibility must be a boolean')
    set({ recentsOpen })
  },
  setSettingsOpen: (settingsOpen) => {
    if (typeof settingsOpen !== 'boolean') throw new Error('Settings visibility must be a boolean')
    set({ settingsOpen })
  },
  setMoreSectionOpen: (id, open) => {
    if (!Object.values(MorePanelId).includes(id)) throw new Error(`Invalid More section id: "${String(id)}"`)
    if (typeof open !== 'boolean') throw new Error('More section visibility must be a boolean')
    const moreSectionsOpen = new Set(get().moreSectionsOpen)
    if (open) moreSectionsOpen.add(id)
    else moreSectionsOpen.delete(id)
    set({ moreSectionsOpen })
  },
  setRecoveryBlockedEditId: (recoveryBlockedEditId) => {
    if (recoveryBlockedEditId !== null && !recoveryBlockedEditId.trim()) {
      throw new Error('Recovery-blocked edit id must be null or a non-empty string')
    }
    set({ recoveryBlockedEditId })
  },
}))
